import { AppConfig } from '../config/AppConfig';
import type { StoredTokens } from './TokenStore';

export interface AuthUrlParams {
  redirectUri: string;
  state: string;
  codeChallenge: string;
  /** Optional override; defaults to config scopes */
  scopes?: string[];
  /** Optional; defaults to 'code' */
  responseType?: 'code';
  /** Optional OAuth prompt parameter (e.g. 'login' to force re-authentication) */
  prompt?: 'login' | 'consent' | 'none';
}

export interface TokenResponse {
  access_token: string;
  id_token: string;
  token_type: 'Bearer' | string;
  expires_in: number;
  refresh_token?: string;
}

export type TokenExchangeResult =
  | { ok: true; tokens: StoredTokens }
  | { ok: false; status: number; error: string };

export const AuthService = {
  buildAuthorizeUrl(params: AuthUrlParams): string {
    const config = AppConfig.get();
    const base = config.cognito.hostedUiBaseUrl;
    const scopes = params.scopes ?? config.cognito.scopes;
    const url = new URL(`${base}/oauth2/authorize`);
    url.searchParams.set('client_id', config.cognito.clientId);
    url.searchParams.set('response_type', params.responseType ?? 'code');
    url.searchParams.set('redirect_uri', params.redirectUri);
    url.searchParams.set('scope', scopes.join(' '));
    url.searchParams.set('state', params.state);
    url.searchParams.set('code_challenge_method', 'S256');
    url.searchParams.set('code_challenge', params.codeChallenge);
    if (params.prompt) {
      url.searchParams.set('prompt', params.prompt);
    }
    return url.toString();
  },

  async exchangeCodeForTokens(
    code: string,
    codeVerifier: string,
  ): Promise<TokenExchangeResult> {
    const config = AppConfig.get();
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.cognito.clientId,
      code,
      redirect_uri: config.cognito.redirectUri,
      code_verifier: codeVerifier,
    });
    try {
      const resp = await fetch(config.cognito.tokenUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!resp.ok) {
        return { ok: false, status: resp.status, error: 'Token exchange failed' };
      }
      const data = (await resp.json()) as TokenResponse;
      const nowSeconds = Math.floor(Date.now() / 1000);
      const tokens: StoredTokens = {
        idToken: data.id_token,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresAt: nowSeconds + data.expires_in,
        tokenType: 'Bearer',
      };
      return { ok: true, tokens };
    } catch {
      return { ok: false, status: 0, error: 'Network error during token exchange' };
    }
  },

  buildLogoutUrl(returnTo?: string): string {
    const config = AppConfig.get();
    const url = new URL(config.cognito.logoutUrl);
    url.searchParams.set('client_id', config.cognito.clientId);
    url.searchParams.set(
      'logout_uri',
      returnTo ?? config.cognito.postLogoutRedirectUri,
    );
    return url.toString();
  },
};
